import { useCallback, useEffect, useRef, useState } from 'react';
import { logCamera } from '../../../services/camera-manager';

// Gives iOS a beat to release the camera hardware before the next
// getUserMedia call — retrying instantly is what tends to leave it stuck.
const RECOVERY_DELAY_MS = 1500;

/**
 * Single camera recovery path. Call `recoverCamera(reason, error)` from
 * anywhere that detects a broken stream (a failed acquisition, track `ended`,
 * a visibility health check) — it debounces into one reacquire instead of
 * letting callers race each other.
 *
 * `isStreamAlive` / `onRecoveryCancelled` are the iOS guard: a reacquire here
 * means a fresh getUserMedia, and on iOS Safari a fresh getUserMedia mid-session
 * is a fresh permission prompt. WebKit interrupts a capture session for reasons
 * that heal on their own (the tab going to the background, another app taking
 * the camera for a moment, the video element being moved in the DOM), so before
 * spending a reacquire we re-check whether the stream is actually still dead. If
 * it recovered by itself, the reacquire — and the prompt — is skipped.
 *
 * `onRecover` does the actual reacquisition (a CameraManager call, plus
 * reassigning the caller's video element) — this hook only owns *when* to
 * call it. CameraManager's own deviceId -> facingMode fallback chain (see
 * services/camera/constraints.js) runs unconditionally inside that call, so
 * this hook no longer needs to track or pass along a "use fallback
 * constraints" flag itself.
 */
const useReoOpenCameraWhenHasError = ({ isStreamAlive, onRecoveryCancelled, onRecover } = {}) => {
  const [cameraError, setCameraError] = useState(false);
  const reopeningRef = useRef(false);

  const recoverCamera = useCallback((reason, error) => {
    if (reopeningRef.current) {
      logCamera('recovery-skipped-in-progress', { reason });
      return;
    }

    reopeningRef.current = true;
    logCamera('recovery-started', {
      reason,
      errorName: error?.name,
      errorMessage: error?.message,
    });

    setCameraError(true);
  }, []);

  // Read through refs so the timeout below always sees the latest callbacks
  // without making them dependencies — a churning identity there would restart
  // the recovery delay on every parent render.
  const isStreamAliveRef = useRef(isStreamAlive);
  isStreamAliveRef.current = isStreamAlive;
  const onRecoveryCancelledRef = useRef(onRecoveryCancelled);
  onRecoveryCancelledRef.current = onRecoveryCancelled;
  const onRecoverRef = useRef(onRecover);
  onRecoverRef.current = onRecover;

  useEffect(() => {
    if (!cameraError) {
      return undefined;
    }

    const retryTimeout = setTimeout(() => {
      setCameraError(false); // Reset error state to stop retrying
      reopeningRef.current = false;

      // Healed on its own during the delay — keep the existing stream instead
      // of throwing it away for a new getUserMedia (and, on iOS, a new
      // permission prompt).
      if (isStreamAliveRef.current?.()) {
        logCamera('recovery-cancelled-stream-alive');
        onRecoveryCancelledRef.current?.();
        return;
      }

      logCamera('recovery-reacquire');
      onRecoverRef.current?.();
    }, RECOVERY_DELAY_MS);

    return () => clearTimeout(retryTimeout);
  }, [cameraError]);

  return { recoverCamera, cameraError };
};

export default useReoOpenCameraWhenHasError;
